import { AuthApiPath } from "@/lib/auth/constants";
import {
  buildSafeUrlWithBase,
  QueryParams,
} from "@/utils/functions/buildSafeUrl";
import { getEnv } from "@/env";

type OptionalParams = Partial<{
  additionalPaths: string[];
  qps: QueryParams;
  endingSlash: boolean;
}>;

/**
 * Builds a complete and safe API endpoint URL by combining the base API URL, the provided path,
 * optional additional path segments, and query parameters.
 *
 * @param {AuthApiPath} path - The base API path, which may be modified based on the configuration.
 * @param {OptionalParams} config - Additional configuration options for constructing the endpoint:
 *  - `additionalPaths` (optional): An array of additional path segments to append to the base path.
 *  - `qps` (optional): Query parameters to include in the endpoint URL.
 *
 * @returns {string} A fully constructed, safe API endpoint URL.
 *
 * @example
 * const path = "/v1/resource";
 * const config = {
 *   additionalPaths: ["subresource"],
 *   qps: { key: "value" },
 * };
 * const apiEndpoint = buildApiEndpoint(path, config);
 * console.log(apiEndpoint);
 * // Output: "https://your-base-url/v1/resource/subresource?key=value"
 */
export function buildApiEndpoint(path: AuthApiPath, config?: OptionalParams) {
  const baseUrl = new URL(getEnv("NEXT_PUBLIC_LMS_BASE_URL"));
  const constitutedPath = config?.additionalPaths
    ? [path.replace(/\/$/, ""), ...config.additionalPaths].join("/")
    : path;
  return buildSafeUrlWithBase(
    baseUrl,
    config?.endingSlash ? `${constitutedPath}/` : constitutedPath,
    config?.qps,
  );
}
